import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { stk } from '../../styles/stocktakeUi';
import { StocktakeService } from '../../api/services/stocktakeService';
import { CountTargetsPanel, computeCountProgress } from '../Warehouse/countTargets';
import {
  IMovementCheckLine,
  IMovementCheckResponse,
  ISessionDetailLine,
  ISessionDetailResponse,
  ISessionSummary,
  IStocktakeLocation,
} from '../../types/api/response/stocktakeresponse';
import { ILineAdjustment } from '../../types/api/request/stocktakerequest';

type SortKey = 'variance' | 'age' | 'bin';

const STALE_HOURS = 12;

// Structured reject reasons — must match the backend's accepted codes.
const REASON_OPTIONS = [
  { value: 'MISCOUNT', label: 'Miscount' },
  { value: 'DAMAGE', label: 'Damage' },
  { value: 'THEFT', label: 'Theft' },
  { value: 'EXPIRY_WRITEOFF', label: 'Expiry write-off' },
  { value: 'SYSTEM_ERROR', label: 'System error' },
  { value: 'OTHER', label: 'Other' },
];

const errMessage = (e: unknown): string => {
  const err = e as { response?: { data?: { message?: string } }; message?: string };
  return err?.response?.data?.message ?? err?.message ?? 'Something went wrong.';
};

const fmtDate = (d: string | null): string => {
  if (!d) return '—';
  const [y, m, day] = d.slice(0, 10).split('-');
  return y && m && day ? `${day}/${m}/${y}` : d;
};

const fmtMoney = (n: number | null | undefined): string => (n == null ? '—' : `$${n.toFixed(2)}`);
// Signed money for the NET effect — distinguishes a real loss (−) / gain (+) from a wash ($0.00).
const fmtNet = (n: number | null | undefined): string => {
  if (n == null) return '—';
  const v = Math.abs(n);
  return n < -0.005 ? `-$${v.toFixed(2)}` : n > 0.005 ? `+$${v.toFixed(2)}` : '$0.00';
};

// Show the SKU under a title only when a real product name is the title — otherwise the title already
// IS the SKU, so repeating it is redundant.
const skuCaption = (sku: string, name: string | null | undefined): string =>
  name && name !== sku ? sku : '';

const fmtQty = (n: number | null | undefined): string => (n == null ? '—' : String(Number(n)));

const hoursSince = (iso: string | null): number => {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 0;
  return (Date.now() - t) / 3_600_000;
};

const relativeAge = (iso: string | null): string => {
  const h = hoursSince(iso);
  if (h < 1) return `${Math.max(1, Math.round(h * 60))}m ago`;
  if (h < 24) return `${Math.round(h)}h ago`;
  return `${Math.round(h / 24)}d ago`;
};

// The logged-in admin, for the approver identity (drives the self-approval block server-side).
const currentUser = (): { id?: number; name?: string } => {
  try {
    const raw = localStorage.getItem('ss_adm_pnl_user');
    if (!raw) return {};
    const u = JSON.parse(raw) as {
      id?: number;
      first_name?: string;
      last_name?: string;
      username?: string;
    };
    const name = [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username;
    return { id: u.id, name };
  } catch {
    return {};
  }
};

/**
 * Supervisor approval board (two-pane). Left: the queue of over-threshold counts parked in
 * PENDING_APPROVAL. Right: the selected count's lines with variance + $ value, plus a movement-check
 * that re-reads current Cin7 stock — a count whose bin has moved since it was made can't be approved
 * (it would silently reverse the movement) unless the supervisor consciously overrides.
 */
export const StocktakeApprovalsPage = () => {
  const [pending, setPending] = useState<ISessionSummary[]>([]);
  const [loadingQueue, setLoadingQueue] = useState(false);
  const [queueError, setQueueError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortKey>('variance');

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<ISessionDetailResponse | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const [movement, setMovement] = useState<IMovementCheckResponse | null>(null);
  const [movementLoading, setMovementLoading] = useState(false);
  // The movement check ERRORED (Cin7 unreachable etc.) — distinct from "ran and found no movement".
  // Gates approve on its own: an unverified baseline must never be approved.
  const [movementFailed, setMovementFailed] = useState(false);

  const [note, setNote] = useState('');
  const [reasonCode, setReasonCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // Whether this approver may CORRECT counted quantities (Ron / 'Stocktake Adjust'). The gateway is
  // the real guard; this only decides whether the editable-count affordance is shown.
  const [canAdjust, setCanAdjust] = useState(false);
  // Whether this approver may approve a count THEY made themselves (Ron / 'Stocktake Self Approve').
  // Again a UX hint only — the backend enforces the separation-of-duties block regardless.
  const [canSelfApprove, setCanSelfApprove] = useState(false);
  // Approver's in-progress edits for the open session, keyed by `sku|batch|expiry` → raw input string.
  const [edits, setEdits] = useState<Record<string, string>>({});

  const loadQueue = useCallback(async (silent = false) => {
    if (!silent) setLoadingQueue(true);
    setQueueError(null);
    try {
      const res = await StocktakeService.getSessions({ status: 'PENDING_APPROVAL', size: 100 });
      setPending(res.content ?? []);
    } catch (e) {
      if (!silent) setQueueError(errMessage(e));
    } finally {
      if (!silent) setLoadingQueue(false);
    }
  }, []);

  useEffect(() => {
    void loadQueue();
    // Poll every 60s so new over-threshold counts appear without a manual refresh. Reads our own DB
    // only — NO Cin7 calls (the movement check, which does hit Cin7, only runs when a row is opened).
    const timer = setInterval(() => void loadQueue(true), 60000);
    return () => clearInterval(timer);
  }, [loadQueue]);

  // Daily/weekly target strip. Loaded ONCE on mount (not in the 60s poll — /locations does touch
  // Cin7 for the lot lookup); missing data just hides the strip.
  const [targetLocations, setTargetLocations] = useState<IStocktakeLocation[]>([]);
  useEffect(() => {
    StocktakeService.getLocations()
      .then((res) => setTargetLocations(res.locations ?? []))
      .catch(() => setTargetLocations([]));
  }, []);

  // This approver's Ron-only capabilities (correct counts / approve own count). Once on mount;
  // any failure = no capability, so the affordances stay hidden and the backend stays the guard.
  useEffect(() => {
    StocktakeService.getCapabilities()
      .then((c) => {
        setCanAdjust(!!c.can_adjust);
        setCanSelfApprove(!!c.can_self_approve);
      })
      .catch(() => {
        setCanAdjust(false);
        setCanSelfApprove(false);
      });
  }, []);

  const runMovementCheck = useCallback(async (id: number) => {
    setMovementLoading(true);
    try {
      setMovement(await StocktakeService.movementCheck(id));
      setMovementFailed(false);
    } catch (e) {
      // A failed movement-check is non-fatal for VIEWING, but approve must stay blocked (fail-closed):
      // without a live read we cannot know the bin hasn't moved, and approving would post a count
      // against an unverified baseline. `moved: true` alone does NOT block — the empty `lines` array
      // makes adjustImpossible false — so the failure is tracked explicitly and gates approve itself.
      setMovementFailed(true);
      setMovement({
        session_id: id,
        bin_code: '',
        location_name: '',
        moved: true,
        checked_at: null,
        message: `Could not verify current stock: ${errMessage(e)}`,
        lines: [],
      });
    } finally {
      setMovementLoading(false);
    }
  }, []);

  const select = useCallback(
    async (id: number) => {
      setSelectedId(id);
      setDetail(null);
      setMovement(null);
      setMovementFailed(false); // never carry one count's failed check onto the next
      setNote('');
      setReasonCode('');
      setEdits({}); // never carry one count's approver edits onto the next
      setActionError(null);
      setLoadingDetail(true);
      setDetailError(null);
      try {
        const d = await StocktakeService.getSessionDetail(id);
        setDetail(d);
      } catch (e) {
        setDetailError(errMessage(e));
      } finally {
        setLoadingDetail(false);
      }
      void runMovementCheck(id);
    },
    [runMovementCheck],
  );

  const sorted = useMemo(() => {
    const rows = [...pending];
    rows.sort((a, b) => {
      if (sortBy === 'variance') return (b.variance_value ?? 0) - (a.variance_value ?? 0);
      if (sortBy === 'age') return hoursSince(b.created_at) - hoursSince(a.created_at);
      return (a.bin_code ?? '').localeCompare(b.bin_code ?? '');
    });
    return rows;
  }, [pending, sortBy]);

  const staleCount = useMemo(
    () => pending.filter((s) => hoursSince(s.created_at) > STALE_HOURS).length,
    [pending],
  );

  const detailLines = useMemo(() => {
    if (!detail?.lines) return [];
    return [...detail.lines].sort(
      (a, b) => Math.abs(b.line_value ?? 0) - Math.abs(a.line_value ?? 0),
    );
  }, [detail]);

  // NET effect = sum of the SIGNED per-line values. The header "variance at cost" is the ABSOLUTE
  // sum (so offsetting errors can't hide), which over-states a batch move: e.g. a product re-dated
  // from one expiry to another shows a big gross but nets to $0. Showing the net makes a wash obvious.
  const netValue = useMemo(
    () => (detail?.lines ?? []).reduce((s, l) => s + (l.line_value ?? 0), 0),
    [detail],
  );
  const grossValue = detail?.variance_value ?? 0;
  const hasOffset = grossValue > Math.abs(netValue) + 0.005; // opposite +/− lines cancel out

  // ── Approver corrections (Ron only) ────────────────────────────────────────
  const lineKey = (l: ISessionDetailLine): string =>
    `${l.sku}|${l.batch_sn ?? ''}|${l.expiry ?? ''}`;
  // A line the counter left blank (counted_qty null). The backend treats these as "no change" and
  // refuses to adjust them, so the FE must NOT coalesce them to 0 (that would show a phantom
  // negative variance) and must NOT let them be edited.
  const isUncounted = (l: ISessionDetailLine): boolean => !l.is_addition && l.counted_qty == null;
  // The raw edit typed by the approver, if any (before validation).
  const rawEdit = (l: ISessionDetailLine): string | undefined => edits[lineKey(l)];
  // A raw edit becomes a real correction only when it parses to a non-negative number.
  const parsedEdit = (l: ISessionDetailLine): number | null => {
    const raw = rawEdit(l);
    if (raw == null || raw.trim() === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? n : null;
  };
  // A non-blank field that does NOT parse to a valid qty (negative, letters, etc.).
  const isInvalidEdit = (l: ISessionDetailLine): boolean => {
    const raw = rawEdit(l);
    return raw != null && raw.trim() !== '' && parsedEdit(l) === null;
  };
  const effectiveQty = (l: ISessionDetailLine): number => parsedEdit(l) ?? l.counted_qty ?? 0;
  const isEdited = (l: ISessionDetailLine): boolean => {
    const n = parsedEdit(l);
    return n != null && n !== (l.counted_qty ?? 0);
  };
  // Effective variance/value for display. Uncounted-and-unedited lines stay "no change" (use the
  // backend figures) rather than a fabricated counted−system negative.
  const effVariance = (l: ISessionDetailLine): number =>
    l.is_addition || isUncounted(l) ? (l.variance ?? 0) : effectiveQty(l) - (l.system_qty ?? 0);
  const effLineValue = (l: ISessionDetailLine): number =>
    l.is_addition || isUncounted(l) ? (l.line_value ?? 0) : effVariance(l) * (l.unit_cost ?? 0);

  const adjustmentPayload: ILineAdjustment[] = (detail?.lines ?? [])
    .filter((l) => !l.is_addition && !isUncounted(l) && isEdited(l))
    .map((l) => ({
      sku: l.sku,
      batch_sn: l.batch_sn,
      expiry: l.expiry,
      adjusted_qty: parsedEdit(l) as number,
    }));
  const hasEdits = adjustmentPayload.length > 0;
  const hasInvalidEdit = (detail?.lines ?? []).some((l) => !isUncounted(l) && isInvalidEdit(l));
  // With edits, recompute the header from the edited numbers; otherwise trust the backend figures.
  const effGross = hasEdits
    ? (detail?.lines ?? []).reduce((s, l) => s + Math.abs(effLineValue(l)), 0)
    : grossValue;
  const effNet = hasEdits
    ? (detail?.lines ?? []).reduce((s, l) => s + effLineValue(l), 0)
    : netValue;

  // sku -> product name (from the detail lines) so the movement table can show names, not just SKUs.
  const nameBySku = useMemo(
    () => new Map((detail?.lines ?? []).map((l) => [l.sku, l.name])),
    [detail],
  );

  // line key -> movement line, so each detail row can show its live drift inline ("65 → now 54").
  // The "System" column is the on-hand FROZEN at count time; this surfaces what it is now.
  const movementByKey = useMemo(() => {
    const m = new Map<string, IMovementCheckLine>();
    for (const l of movement?.lines ?? []) {
      m.set(`${l.sku}|${l.batch_sn ?? ''}|${l.expiry ?? ''}`, l);
    }
    return m;
  }, [movement]);

  const afterDecision = async () => {
    setDetail(null);
    setMovement(null);
    setSelectedId(null);
    await loadQueue();
  };

  const doApprove = async () => {
    if (!detail) return;
    const me = currentUser();
    setBusy(true);
    setActionError(null);
    try {
      await StocktakeService.approve(detail.id, {
        approved_by_id: me.id,
        approved_by_name: me.name,
        note: note.trim() || undefined,
        adjust_for_movement: movement?.moved === true || undefined,
        line_adjustments: canAdjust && hasEdits ? adjustmentPayload : undefined,
      });
      setToast(
        `Bin ${detail.bin_code} approved${hasEdits ? ' with changes' : ''} and posted to Cin7.`,
      );
      await afterDecision();
    } catch (e) {
      setActionError(errMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const doReject = async () => {
    if (!detail) return;
    if (!reasonCode) {
      setActionError('Pick a reject reason first.');
      return;
    }
    const me = currentUser();
    setBusy(true);
    setActionError(null);
    try {
      await StocktakeService.reject(detail.id, {
        approved_by_id: me.id,
        approved_by_name: me.name,
        reason_code: reasonCode,
        note: note.trim() || undefined,
      });
      setToast(`Bin ${detail.bin_code} rejected — sent back for recount.`);
      await afterDecision();
    } catch (e) {
      setActionError(errMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const moved = movement?.moved === true;
  // Preview of the movement adjustment: moved lines post (effective count) + delta. The base must be
  // the EFFECTIVE (Ron-edited) quantity — the backend applies the approver's edit BEFORE the movement
  // delta (adjusted = edited + delta), so previewing off the counter's raw number would show, and
  // gate on, the wrong figure.
  //
  // The preview + adjustImpossible gate must mirror the BACKEND's movement scope exactly: the backend
  // only movement-adjusts lines that are in the submission — i.e. non-addition lines the counter
  // actually counted. So (1) build the base map from non-addition lines ONLY, so a same-SKU "found"
  // addition (which shares the key `sku||` for plain-FIFO) can't overwrite the counted line's base;
  // and (2) gate the preview to counted keys only, so a blank/uncounted line that happened to move
  // (a sale before approval) doesn't fabricate a "Will post −N" and falsely block a valid approval.
  const countedKeys = new Set(
    (detail?.lines ?? [])
      .filter((l) => !l.is_addition && l.counted_qty != null)
      .map((l) => lineKey(l)),
  );
  const effByKey = new Map(
    (detail?.lines ?? [])
      .filter((l) => !l.is_addition && l.counted_qty != null)
      .map((l) => [lineKey(l), effectiveQty(l)]),
  );
  const adjustedPreview = (movement?.lines ?? [])
    .filter((l) => {
      const k = `${l.sku}|${l.batch_sn ?? ''}|${l.expiry ?? ''}`;
      return l.moved && (l.delta ?? 0) !== 0 && countedKeys.has(k);
    })
    .map((l) => {
      const base =
        effByKey.get(`${l.sku}|${l.batch_sn ?? ''}|${l.expiry ?? ''}`) ?? l.counted_qty ?? 0;
      return { sku: l.sku, counted: base, delta: l.delta ?? 0, adjusted: base + (l.delta ?? 0) };
    });
  // More moved out than the (edited) count says existed — adjustment impossible, only a recount is honest.
  const adjustImpossible = adjustedPreview.some((a) => a.adjusted < 0);
  // Did any COUNTED line actually move? (Blank/uncounted lines can move too — a sale before approval —
  // but they aren't posted, so they must NOT drive the "approve adjusted" banner/label. See audit-2.)
  const countedMoved = adjustedPreview.length > 0;
  // Separation of duties: a supervisor may not approve a count they made themselves — UNLESS they hold
  // the Ron-only self-approve capability. The backend enforces this regardless; this mirror just
  // disables the button with a clear reason instead of letting the approve call bounce with a 4xx.
  const myId = currentUser().id;
  const isOwnCount = detail?.counted_by_id != null && myId != null && detail.counted_by_id === myId;
  const selfApproveBlocked = isOwnCount && !canSelfApprove;
  // movementFailed blocks on its own — `moved && adjustImpossible` cannot catch it, because a failed
  // check has no lines, so adjustImpossible is false and approve would wrongly go live (fail-open).
  // A syntactically invalid edit (negative/garbage) also blocks — it can't be posted.
  const approveDisabled =
    busy ||
    loadingDetail ||
    movementLoading ||
    movementFailed ||
    hasInvalidEdit ||
    selfApproveBlocked ||
    (moved && adjustImpossible);

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ mb: 2, pb: 2, borderBottom: `1px solid ${stk.line}` }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: stk.ink }}>
          Stocktake Approvals
        </Typography>
        <Typography variant="caption" sx={{ color: stk.inkSoft }}>
          {pending.length} count{pending.length === 1 ? '' : 's'} waiting
          {staleCount > 0 ? ` · ${staleCount} over ${STALE_HOURS}h old` : ''}
        </Typography>
        {targetLocations.length > 0 && (
          <Box sx={{ mt: 0.5 }}>
            <CountTargetsPanel progress={computeCountProgress(targetLocations)} compact />
          </Box>
        )}
      </Box>
      <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            select
            size="small"
            label="Sort"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortKey)}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="variance">Variance $ (high→low)</MenuItem>
            <MenuItem value="age">Oldest first</MenuItem>
            <MenuItem value="bin">Bin</MenuItem>
          </TextField>
          <Button size="small" onClick={() => loadQueue()} disabled={loadingQueue}>
            Refresh
          </Button>
        </Stack>
      </Stack>

      {toast && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setToast(null)}>
          {toast}
        </Alert>
      )}
      {queueError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setQueueError(null)}>
          {queueError}
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          alignItems: 'flex-start',
        }}
      >
        {/* ── Queue (left) ── */}
        <Box sx={{ width: { xs: '100%', md: 380 }, flexShrink: 0 }}>
          {loadingQueue && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
              <CircularProgress size={26} />
            </Box>
          )}
          {!loadingQueue && pending.length === 0 && (
            <Typography color="text.secondary" sx={{ py: 3 }}>
              No counts waiting for approval. 🎉
            </Typography>
          )}
          <Stack spacing={1.5}>
            {sorted.map((s) => {
              const isStale = hoursSince(s.created_at) > STALE_HOURS;
              const big = (s.variance_value ?? 0) >= 100;
              return (
                <Card
                  key={s.id}
                  elevation={0}
                  sx={{
                    border: `1px solid ${s.id === selectedId ? stk.blue : stk.line}`,
                    borderLeft: `4px solid ${s.id === selectedId ? stk.blue : big ? stk.red : stk.amberLine}`,
                    borderRadius: 2,
                    bgcolor: s.id === selectedId ? stk.blueBg : '#fff',
                    transition: 'background-color .12s ease, border-color .12s ease',
                  }}
                >
                  <CardActionArea onClick={() => select(s.id)}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 700, fontFamily: stk.mono, letterSpacing: 0.3 }}
                        >
                          {s.bin_code}
                        </Typography>
                        <Box
                          sx={{
                            px: 1.25,
                            py: 0.25,
                            borderRadius: 99,
                            bgcolor: big ? stk.redBg : stk.slateBg,
                            border: `1px solid ${big ? stk.redLine : stk.line}`,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, color: big ? stk.red : stk.ink }}
                          >
                            {fmtMoney(s.variance_value)}
                          </Typography>
                        </Box>
                      </Stack>
                      <Typography variant="body2" sx={{ color: stk.inkSoft }}>
                        {s.counted_by_name ?? '—'} · {s.line_count ?? 0} line(s)
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: isStale ? stk.red : stk.inkSoft,
                          fontWeight: isStale ? 700 : 400,
                        }}
                      >
                        {isStale ? '⏱ ' : ''}
                        {relativeAge(s.created_at)} · {s.location_name.split(' - ')[0]}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              );
            })}
          </Stack>
        </Box>

        {/* ── Detail (right) ── */}
        <Box sx={{ flexGrow: 1, width: '100%' }}>
          {selectedId && (
            <Card elevation={0} sx={{ border: `1px solid ${stk.line}`, borderRadius: 2 }}>
              <CardContent>
                {loadingDetail && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                    <CircularProgress size={26} />
                  </Box>
                )}
                {detailError && <Alert severity="error">{detailError}</Alert>}

                {detail && (
                  <>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Box>
                        <Typography
                          variant="h5"
                          sx={{ fontWeight: 800, fontFamily: stk.mono, letterSpacing: 0.3 }}
                        >
                          {detail.bin_code}
                        </Typography>
                        <Typography variant="body2" sx={{ color: stk.inkSoft }}>
                          {detail.location_name} · counted by <b>{detail.counted_by_name ?? '—'}</b>{' '}
                          · {fmtDate(detail.created_at)}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          textAlign: 'right',
                          px: 2,
                          py: 1,
                          borderRadius: 2.5,
                          bgcolor: stk.redBg,
                          border: `1px solid ${stk.redLine}`,
                        }}
                      >
                        <Typography variant="caption" sx={{ color: stk.inkSoft, display: 'block' }}>
                          Variance at cost{hasEdits ? ' (edited)' : ''}
                        </Typography>
                        <Typography variant="h6" sx={{ color: stk.red, fontWeight: 800 }}>
                          {fmtMoney(effGross)}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: stk.inkSoft, display: 'block', mt: 0.25 }}
                        >
                          Net effect{' '}
                          <Box
                            component="span"
                            sx={{
                              fontWeight: 700,
                              color:
                                Math.abs(effNet) < 0.005
                                  ? stk.inkSoft
                                  : effNet < 0
                                    ? stk.red
                                    : stk.green,
                            }}
                          >
                            {fmtNet(effNet)}
                          </Box>
                          {!hasEdits && hasOffset && ' · lines offset'}
                        </Typography>
                      </Box>
                    </Stack>

                    {detail.note && (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        <strong>Counter's note:</strong> {detail.note}
                      </Alert>
                    )}

                    {/* Movement check */}
                    <Box sx={{ mt: 2 }}>
                      {movementLoading && (
                        <Alert severity="info" icon={<CircularProgress size={18} />}>
                          Checking current stock in Cin7…
                        </Alert>
                      )}
                      {!movementLoading && moved && (
                        <Alert
                          severity={
                            adjustImpossible
                              ? 'error'
                              : countedMoved || movementFailed
                                ? 'warning'
                                : 'info'
                          }
                        >
                          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                            {adjustImpossible
                              ? 'Stock moved since the count and contradicts it — reject for a recount.'
                              : countedMoved
                                ? 'Stock sold/moved since this count. Approving posts these adjusted numbers:'
                                : movementFailed
                                  ? ''
                                  : 'Stock moved on lines that weren’t counted — the counted lines are unaffected; safe to approve.'}
                          </Typography>
                          {adjustedPreview.length > 0 && (
                            <Table
                              size="small"
                              sx={{ maxWidth: 460, '& td, & th': { border: 0, py: 0.25 } }}
                            >
                              <TableHead>
                                <TableRow>
                                  <TableCell>Product</TableCell>
                                  <TableCell align="right">Counted</TableCell>
                                  <TableCell align="right">Moved since</TableCell>
                                  <TableCell align="right">Will post</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {adjustedPreview.map((a, i) => (
                                  <TableRow key={`${a.sku}-adj-${i}`}>
                                    <TableCell>
                                      <Typography variant="body2">
                                        {nameBySku.get(a.sku) ?? a.sku}
                                      </Typography>
                                      {skuCaption(a.sku, nameBySku.get(a.sku)) && (
                                        <Typography variant="caption" color="text.secondary">
                                          {a.sku}
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell align="right">{a.counted}</TableCell>
                                    <TableCell align="right">
                                      {a.delta > 0 ? `+${a.delta}` : a.delta}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                                      {a.adjusted}
                                      {a.adjusted < 0 ? ' ❌' : ''}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                          {/* moved lines with no quantity delta (e.g. allocation changes) + fail-closed fallback */}
                          {(movement?.lines ?? [])
                            .filter((l) => l.moved && (l.delta ?? 0) === 0)
                            .map((l, i) => (
                              <Typography variant="body2" key={`${l.sku}-nr-${i}`} sx={{ mt: 0.5 }}>
                                <b>{nameBySku.get(l.sku) ?? l.sku}</b>
                                {l.reason ? ` — ${l.reason}` : ''}
                              </Typography>
                            ))}
                          {adjustedPreview.length === 0 &&
                            (movement?.lines ?? []).filter((l) => l.moved).length === 0 &&
                            movement?.message && (
                              <Typography variant="body2">{movement.message}</Typography>
                            )}
                        </Alert>
                      )}
                      {!movementLoading && !moved && (
                        <Alert severity="success">No stock movement since the count.</Alert>
                      )}
                      <Button
                        size="small"
                        onClick={() => runMovementCheck(detail.id)}
                        disabled={movementLoading}
                        sx={{ mt: 0.5 }}
                      >
                        Re-check movement
                      </Button>
                    </Box>

                    {/* Lines */}
                    <Box sx={{ mt: 2, overflowX: 'auto' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow
                            sx={{
                              '& th': {
                                bgcolor: stk.slateBg,
                                fontWeight: 700,
                                color: stk.inkSoft,
                                textTransform: 'uppercase',
                                fontSize: 11,
                                letterSpacing: 0.6,
                              },
                            }}
                          >
                            <TableCell>Product</TableCell>
                            <TableCell>Batch / Exp</TableCell>
                            <TableCell align="right">Counted</TableCell>
                            <TableCell align="right">System (at count)</TableCell>
                            <TableCell align="right">Variance</TableCell>
                            <TableCell align="right">$ Value</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {detailLines.map((l, i) => {
                            const edited = isEdited(l);
                            const invalid = isInvalidEdit(l);
                            const effV = effVariance(l);
                            const effVal = effLineValue(l);
                            const shortfall = effVal < 0;
                            const mv = movementByKey.get(
                              `${l.sku}|${l.batch_sn ?? ''}|${l.expiry ?? ''}`,
                            );
                            const drift = !!mv && mv.moved && (mv.delta ?? 0) !== 0;
                            // Only a line the counter actually counted can be corrected — the backend
                            // refuses to adjust a blank (uncounted) line, so it stays read-only here.
                            const editable = canAdjust && !l.is_addition && l.counted_qty != null;
                            return (
                              <TableRow
                                key={`${l.sku}-${i}`}
                                sx={shortfall ? { bgcolor: 'rgba(211,47,47,0.06)' } : undefined}
                              >
                                <TableCell>
                                  <Typography variant="body2">
                                    {l.name ?? l.sku}
                                    {l.is_addition && (
                                      <Chip
                                        label="found"
                                        size="small"
                                        color="info"
                                        sx={{ ml: 0.5 }}
                                      />
                                    )}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {skuCaption(l.sku, l.name)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  {l.expiry ? fmtDate(l.expiry) : (l.batch_sn ?? '—')}
                                </TableCell>
                                <TableCell align="right">
                                  {editable ? (
                                    <>
                                      <TextField
                                        type="number"
                                        size="small"
                                        variant="standard"
                                        error={invalid}
                                        helperText={invalid ? 'Enter a number ≥ 0' : undefined}
                                        value={edits[lineKey(l)] ?? String(l.counted_qty ?? 0)}
                                        onChange={(e) =>
                                          setEdits((prev) => ({
                                            ...prev,
                                            [lineKey(l)]: e.target.value,
                                          }))
                                        }
                                        inputProps={{
                                          min: 0,
                                          style: { textAlign: 'right', width: 60 },
                                          'aria-label': `Counted quantity for ${l.name ?? l.sku}`,
                                        }}
                                        sx={
                                          edited
                                            ? { '& input': { color: stk.amber, fontWeight: 700 } }
                                            : undefined
                                        }
                                      />
                                      {edited && (
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            display: 'block',
                                            color: stk.inkSoft,
                                            textDecoration: 'line-through',
                                          }}
                                        >
                                          was {fmtQty(l.counted_qty)}
                                        </Typography>
                                      )}
                                    </>
                                  ) : (
                                    fmtQty(l.counted_qty)
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  {fmtQty(l.system_qty)}
                                  {drift && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        display: 'block',
                                        color: stk.amber,
                                        fontWeight: 700,
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      → now {fmtQty(mv!.system_qty_now)} (
                                      {(mv!.delta ?? 0) > 0 ? '+' : ''}
                                      {fmtQty(mv!.delta)})
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{
                                    color:
                                      effV < 0
                                        ? 'error.main'
                                        : effV > 0
                                          ? 'success.main'
                                          : undefined,
                                  }}
                                >
                                  {effV > 0 ? `+${fmtQty(effV)}` : fmtQty(effV)}
                                </TableCell>
                                <TableCell align="right">{fmtMoney(effVal)}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', mt: 1, color: stk.inkSoft }}
                      >
                        <b>System (at count)</b> is the on-hand when this bin was counted — a frozen
                        snapshot, not live stock. A highlighted <b>“→ now”</b> means the stock
                        changed since the count; <b>Approve adjusted</b> posts the live figure, not
                        the stale one.
                        {canAdjust && (
                          <>
                            {' '}
                            You can <b>edit a Counted quantity</b> to correct a miscount before
                            approving; the counter’s original is kept for the record.
                          </>
                        )}
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {actionError && (
                      <Alert severity="error" sx={{ mb: 1 }} onClose={() => setActionError(null)}>
                        {actionError}
                      </Alert>
                    )}

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
                      <TextField
                        select
                        size="small"
                        label="Reject reason"
                        value={reasonCode}
                        onChange={(e) => setReasonCode(e.target.value)}
                        sx={{ minWidth: 180 }}
                      >
                        {REASON_OPTIONS.map((o) => (
                          <MenuItem key={o.value} value={o.value}>
                            {o.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        fullWidth
                        size="small"
                        label="Notes (optional)"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                      />
                    </Stack>

                    <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
                      <Button
                        color="error"
                        variant="outlined"
                        onClick={doReject}
                        disabled={busy}
                        sx={{ borderRadius: 99, px: 3, fontWeight: 700 }}
                      >
                        Reject
                      </Button>
                      <Tooltip
                        title={
                          selfApproveBlocked
                            ? "You can't approve your own count — another supervisor must approve it."
                            : movementFailed
                              ? "Can't verify current stock in Cin7 right now — approving could overwrite stock that moved. Re-check movement to try again."
                              : moved && adjustImpossible
                                ? 'Movement contradicts the count — reject for a recount.'
                                : ''
                        }
                      >
                        <span>
                          <Button
                            variant="contained"
                            color="success"
                            onClick={doApprove}
                            disabled={approveDisabled}
                            sx={{ borderRadius: 99, px: 3, fontWeight: 700, boxShadow: 'none' }}
                          >
                            {busy
                              ? 'Working…'
                              : hasEdits
                                ? 'Approve with changes & post'
                                : countedMoved
                                  ? 'Approve adjusted & post'
                                  : 'Approve & post'}
                          </Button>
                        </span>
                      </Tooltip>
                    </Stack>
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </Box>
      </Box>
    </Box>
  );
};
